import { KCheckbox } from '@/ui-library/components';
import { KText } from '@/ui-library/components/text';
import { Flex, Tooltip } from '@chakra-ui/react';
import moment from 'moment-timezone';
import React, { memo, useEffect, useState } from 'react';
import isEqual from 'lodash.isequal';
import { useDateFormat } from '@/hooks';

// Common props interface for both headers
interface SchCalHeaderProps {
  date?: Date;
  text?: string;
  dow?: number;
  isDisabled?: boolean;
  isOther?: boolean;
  isToday?: boolean;
  isPast?: boolean;
  isFuture?: boolean;
  onDaySelect?: (date: string, checked: boolean) => void;
  selectedDays?: string[];
  fullDayEventDays: any;
  currentWeekUserAvailability: any;
  timezone: any;
  currentWeekState: any;
  interviewDataRef: any;
  registerForUpdates: any;
  currentWeekRange: any;
  updateSharedData: any;
  setHasUserModified: any;
  disableReason?: string;
  unavailableDays?: string[];
  dataForAllWeeks?: any;
  checkboxProps?: {
    isSelfSchedule?: boolean;
    isCheckedChecked?: boolean;
    isIndeterminate?: boolean;
    onCheckboxChange?: () => void;
  };
}

// Utility function to check interviewer availability
const isAtLeastOneInterviewerAvailableOnTheDay = (
  dateString: string,
  availabilityArray: any[],
  timezone: string,
  dateFormat: { label: string },
  interviewers: any[],
  interviewKind: string,
  dataForAllWeeks: any,
  currentWeekRange: string
) => {
  const targetDate = moment.tz(dateString, dateFormat?.label, timezone);

  // For one-on-one interviews, check if at least one interviewer has availability that OVERLAPS this day
  if (interviewKind === 'one_on_one') {
    // Check individual interviewer availability from the raw data
    const weekData = dataForAllWeeks?.[currentWeekRange] || {};

    return interviewers.some((interviewerId) => {
      const interviewerData = weekData[interviewerId];
      if (!interviewerData?.availability) return false;

      return interviewerData.availability.some((slot: any) => {
        const slotStart = moment.tz(slot.start, timezone);
        const slotEnd = moment.tz(slot.end, timezone);

        // Day boundaries in the selected timezone
        const dayStart = targetDate.clone().startOf('day');
        const dayEnd = targetDate.clone().endOf('day');

        return slotStart.isBefore(dayEnd) && slotEnd.isAfter(dayStart);
      });
    });
  } else {
    // For panel interviews, all interviewers must have availability (existing logic)
    if (!availabilityArray) return false;
    for (const slot of availabilityArray) {
      const slotStartDate = moment.tz(slot.start, timezone);
      if (targetDate.isSame(slotStartDate, 'day')) {
        return true;
      }
    }
    return false;
  }
};

// Utility function to calculate checkbox state
const getCheckboxState = (
  dateStr: string,
  timezone: string,
  currentWeekState: any,
  dateFormat: { label: string }
) => {
  const dateKey = moment(dateStr).tz(timezone).format(dateFormat?.label);

  const dayState = currentWeekState?.days?.[dateKey] || {
    isSelected: false,
    slotsGenerated: [],
    removedSlots: [],
    customSlots: [],
  };

  const isSelected = dayState.isSelected;
  if (!isSelected && dayState?.customSlots?.length > 0) {
    // when initially checkbox not selected and custom slots selected
    return 'intermediate';
  }
  if (!isSelected) return 'unchecked';

  const totalSlots = dayState.slotsGenerated.length;
  const remainingSlots = totalSlots - dayState.removedSlots.length;

  if (remainingSlots === 0) return 'unchecked';
  if (remainingSlots < totalSlots) return 'intermediate';
  return 'checked';
};

// Common UI Component
const CommonCalHeaderUI: React.FC<{
  number: string;
  alphabet: string;
  isToday: boolean;
  isDisabled?: boolean;
  isPast?: boolean;
  disableReason?: string;
  showCheckbox?: boolean;
  isCheckedChecked?: boolean;
  isIndeterminate?: boolean;
  onCheckboxChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}> = memo(
  ({
    number,
    alphabet,
    isToday,
    isDisabled,
    isPast,
    disableReason,
    showCheckbox = false,
    isCheckedChecked,
    isIndeterminate,
    onCheckboxChange,
  }) => (
    <Flex w="100%" justifyContent="space-between" alignItems="flex-start">
      <Flex flexDir="column" alignItems="flex-start">
        <KText
          variant="overhead"
          color={isToday ? 'primary-500' : 'secondary-700'}
        >
          {alphabet}
        </KText>
        <KText
          variant="heading_2"
          color={isToday ? 'primary-500' : 'secondary-700'}
        >
          {number}
        </KText>
      </Flex>
      {showCheckbox && (
        <Tooltip
          label={isDisabled || isPast ? disableReason : ''}
          isDisabled={!(isDisabled || isPast)}
        >
          <span>
            <KCheckbox
              color="secondary-100"
              isChecked={isCheckedChecked}
              isIndeterminate={isIndeterminate}
              onChange={onCheckboxChange}
              isDisabled={isDisabled || isPast}
              style={{
                borderRadius: '4px',
                border:
                  isDisabled &&
                  isToday &&
                  '1px solid var(--kula-colors-primary-200) !important',
              }}
            />
          </span>
        </Tooltip>
      )}
    </Flex>
  )
);

// Self-Schedule Header Component
export const SelfSchCalHeader: React.FC<SchCalHeaderProps> = memo((props) => {
  const {
    date,
    text,
    isToday = true,
    isPast = false,
    currentWeekRange,
    updateSharedData,
    setHasUserModified,
    fullDayEventDays,
    currentWeekUserAvailability,
    registerForUpdates,
    interviewDataRef,
    timezone,
    currentWeekState,
    unavailableDays = [],
    dataForAllWeeks,
  } = props || {};

  const { dateFormat } = useDateFormat();

  const { interviewers, schedulerState, weekAvailability, interviewKind } =
    interviewDataRef.current || {};
  const [localInterviewers, setLocalInterviewers] = useState(interviewers);
  const [localInterviewKind, setLocalInterviewKind] = useState(interviewKind);
  const [localSchedularState, setLocalSchedularState] =
    useState(schedulerState);

  const dateKey = moment.tz(date, timezone).format(dateFormat?.label);
  const isPastDay = moment
    .tz(date, timezone)
    .isBefore(moment().tz(timezone), 'day');
  const hasFullDayEvent = !!fullDayEventDays[dateKey];
  const noInterviewers = localInterviewers.length === 0;
  const isInterviewerNotAvailable = !isAtLeastOneInterviewerAvailableOnTheDay(
    dateKey,
    currentWeekUserAvailability,
    timezone,
    dateFormat,
    localInterviewers,
    localInterviewKind,
    dataForAllWeeks,
    currentWeekRange
  );
  // Check if the entire week is unavailable based on weekAvailability
  const isWeekUnavailable = weekAvailability?.[currentWeekRange] === false;

  const dayState = localSchedularState?.[currentWeekRange]?.days?.[dateKey] || {
    isSelected: false,
    slotsGenerated: [],
    removedSlots: [],
    customSlots: [],
  };

  const isNoAvailabilityOnTheDay = unavailableDays.includes(dateKey);

  // Combine all disable conditions
  const isDisabled =
    isPastDay ||
    hasFullDayEvent ||
    noInterviewers ||
    isInterviewerNotAvailable ||
    isNoAvailabilityOnTheDay ||
    isWeekUnavailable;

  useEffect(() => {
    const unregisterInterviewers = registerForUpdates(
      'interviewers',
      (data) => {
        const { interviewers } = data?.fullData ?? {};
        setLocalInterviewers((prev) =>
          isEqual(prev, interviewers) ? prev : interviewers
        );
      }
    );

    const unregisterInterviewKind = registerForUpdates(
      'interviewKind',
      (data) => {
        const { interviewKind } = data?.fullData ?? {};
        setLocalInterviewKind((prev) =>
          isEqual(prev, interviewKind) ? prev : interviewKind
        );
      }
    );

    const unregisterSchedulerState = registerForUpdates(
      'schedulerState',
      (data) => {
        const { schedulerState } = data?.fullData ?? {};
        setLocalSchedularState((prev) =>
          isEqual(prev, schedulerState) ? prev : schedulerState
        );
      }
    );

    return () => {
      unregisterInterviewers();
      unregisterSchedulerState();
      unregisterInterviewKind();
    };
  }, [registerForUpdates]);

  // Determine the reason for disabling the checkbox

  function getDisableReason() {
    if (!isDisabled) {
      return ''; // No reason to disable if it's not disabled.
    }

    if (isPastDay) {
      return 'You cannot select slots in the past';
    }

    if (noInterviewers) {
      return 'Pick an interviewer to select slots';
    }

    if (isInterviewerNotAvailable || isNoAvailabilityOnTheDay) {
      if (localInterviewers.length === 1) {
        return 'Interviewer is not available on this day';
      } else {
        if (localInterviewKind === 'one_on_one') {
          return 'All interviewers are not available on this day';
        } else return 'Some interviewers are not available on this day';
      }
    }

    if (hasFullDayEvent) {
      return 'Day unavailable due to a full-day event';
    }

    if (isWeekUnavailable) {
      return 'No interviewers available this week';
    }

    return ''; // Default case, though it should ideally not be reached.
  }

  let disableReason = getDisableReason();

  const [number, alphabet] = text?.split(' ') || [];
  const dateStr = moment.tz(date, timezone).toISOString();
  const checkboxState = getCheckboxState(
    dateStr,
    timezone,
    localSchedularState?.[currentWeekRange] || {},
    dateFormat
  );
  const isCheckedChecked = checkboxState === 'checked';
  const isIndeterminate = checkboxState === 'intermediate';

  const handleDaySelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    const day = moment.tz(date, timezone);
    const dateKey = day.format(dateFormat?.label);

    // Prevent changes if the day or week is disabled
    if (
      isPastDay ||
      hasFullDayEvent ||
      interviewers.length === 0 ||
      isWeekUnavailable
    ) {
      return;
    }

    // PRESERVE ALL CUSTOM SLOTS - they take priority
    const filteredCustomSlots = checked ? dayState?.customSlots || [] : [];

    // Filter out generated slots that overlap with custom slots
    const customSlotTimeKeys = filteredCustomSlots.map(
      (customSlot) => customSlot.id.split('custom')[1] // Extract time part like "14/08/2025-04:00-04:30"
    );

    const filteredGeneratedSlots = checked
      ? (dayState?.slotsGenerated || []).filter((generatedSlot) => {
          const hasCustomOverlap = customSlotTimeKeys.includes(
            generatedSlot.id
          );

          return !hasCustomOverlap;
        })
      : dayState?.slotsGenerated || [];

    const newDayState = {
      ...dayState,
      isSelected: checked,
      customSlots: filteredCustomSlots,
      slotsGenerated: filteredGeneratedSlots, // Use filtered generated slots
      removedSlots: checked ? [] : dayState?.removedSlots,
    };

    const updatedDays = {
      ...currentWeekState.days,
      [dateKey]: newDayState,
    };

    setHasUserModified(true);
    updateSharedData({
      schedulerState: {
        ...schedulerState,
        [currentWeekRange]: { ...currentWeekState, days: updatedDays },
      },
      findTimeValue: '',
    });
  };

  return (
    <CommonCalHeaderUI
      number={number}
      alphabet={alphabet}
      isToday={isToday}
      isDisabled={isDisabled}
      isPast={isPast}
      disableReason={disableReason}
      showCheckbox={true}
      isCheckedChecked={isCheckedChecked}
      isIndeterminate={isIndeterminate}
      onCheckboxChange={handleDaySelect}
    />
  );
});

// Manual Header Component (without checkbox)
export const ManualSchCalHeader: React.FC<SchCalHeaderProps> = memo((props) => {
  const { text, isToday = true } = props || {};
  const [number, alphabet] = text?.split(' ') || [];

  return (
    <CommonCalHeaderUI number={number} alphabet={alphabet} isToday={isToday} />
  );
});
